import { createDepartment } from '@/api/departmentsApi';
import { createPosition } from '@/api/positionsApi';
import { createGrade } from '@/api/gradesApi';
import { orgReferenceApi } from '@/api/orgReferenceApi';
import { normalizeReferenceOption } from '../utils/normalizeReferenceOption';
import { REFERENCE_ENTITY_KEYS, REFERENCE_NAME_MAX_LENGTH } from '../constants/referenceEntityKeys';

/**
 * Registry: backend semantics for quick-add.
 * Forms supply context; this file owns payloads and create calls.
 */
export const quickAddReferenceRegistry = {
  [REFERENCE_ENTITY_KEYS.department]: {
    permission: 'departments.manage.global',
    maxLength: REFERENCE_NAME_MAX_LENGTH.department,
    create: createDepartment,
    buildPayload: ({ name }) => ({
      name,
      is_active: 1,
    }),
    normalize: (response) => normalizeReferenceOption('department', response),
  },

  [REFERENCE_ENTITY_KEYS.position]: {
    permission: 'positions.create.global',
    scopedPermission: 'positions.create.department',
    requires: ['departmentId'],
    maxLength: REFERENCE_NAME_MAX_LENGTH.position,
    create: createPosition,
    buildPayload: ({ name, context }) => ({
      name,
      department_id: context.departmentId,
      is_active: 1,
    }),
    normalize: (response) => normalizeReferenceOption('position', response),
  },

  [REFERENCE_ENTITY_KEYS.grade]: {
    permission: 'org.manage_grades',
    maxLength: REFERENCE_NAME_MAX_LENGTH.grade,
    create: createGrade,
    buildPayload: ({ name }) => ({
      name,
      is_active: 1,
    }),
    normalize: (response) => normalizeReferenceOption('grade', response),
  },

  [REFERENCE_ENTITY_KEYS.location]: {
    permission: 'org.manage_locations',
    requires: ['locationType'],
    maxLength: REFERENCE_NAME_MAX_LENGTH.location,
    create: (payload) => orgReferenceApi.locations.create(payload),
    buildPayload: ({ name, context }) => ({
      name,
      type: context.locationType,
      country: context.country || null,
      is_active: 1,
    }),
    normalize: (response) => normalizeReferenceOption('location', response),
  },
  [REFERENCE_ENTITY_KEYS.subDepartment]: {
    permission: 'org.manage_sub_departments',
    requires: ['departmentId'],
    maxLength: REFERENCE_NAME_MAX_LENGTH.subDepartment,
    create: (payload) => orgReferenceApi.subDepartments.create(payload),
    buildPayload: ({ name, context }) => ({
      name,
      department_id: context.departmentId,
      is_active: 1,
    }),
    normalize: (response) => normalizeReferenceOption('subDepartment', response),
  },

  [REFERENCE_ENTITY_KEYS.team]: {
    permission: 'org.manage_teams',
    maxLength: REFERENCE_NAME_MAX_LENGTH.team,
    create: (payload) => orgReferenceApi.teams.create(payload),
    buildPayload: ({ name, context }) => ({
      name,
      department_id: context.departmentId ?? null,
      sub_department_id: context.subDepartmentId ?? null,
      location_id: context.locationId ?? null,
      is_active: 1,
    }),
    normalize: (response) => normalizeReferenceOption('team', response),
  },
};

export function getQuickAddRegistryEntry(entity) {
  const entry = quickAddReferenceRegistry[entity];
  if (!entry) {
    throw new Error(`Unknown quick-add entity: ${entity}`);
  }
  return entry;
}
